import React, { useState, useEffect, useCallback } from 'react';
import { 
  IDateComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormDateTimeProps {
  id: string;
  properties: IDateComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

export const DfFormDateTime: React.FC<DfFormDateTimeProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [value, setValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  // Validate field
  const validateField = useCallback((val: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Get dateTimeMode to determine validation type
    const componentName = (properties as any).name || '';
    const dateTimeMode = (properties.basic as any)?.dateTimeMode || (componentName === 'datetime-picker' ? 'datetime' : 'date');
    const trimmedVal = val ? String(val).trim() : '';

    // Required validation - only check if field is required
    if (properties?.validation?.required && (!trimmedVal || trimmedVal === '')) {
      errors.required = true;
      isValid = false;
      return { isValid, errors };
    }

    // Only validate format if there's a value (non-required fields can be empty)
    if (trimmedVal && trimmedVal !== '') {
      if (dateTimeMode === 'time') {
        // Validate time format (HH:MM or HH:MM:SS)
        // HTML5 time input returns format HH:MM or HH:MM:SS
        const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
        if (!timeRegex.test(trimmedVal)) {
          errors.invalidDate = true;
          isValid = false;
          return { isValid, errors };
        }
        // Time is valid, no errors
      } else if (dateTimeMode === 'datetime') {
        // Validate datetime format
        const selectedDate = new Date(trimmedVal);
        if (isNaN(selectedDate.getTime())) {
          errors.invalidDate = true;
          isValid = false;
          return { isValid, errors };
        }
        
        // Date range validations for datetime
        if (properties?.validation?.minDate) {
          const minDate = new Date(properties.validation.minDate);
          if (selectedDate < minDate) {
            errors.minDate = true;
            errors.minDateMessage = `Date and time must be on or after ${minDate.toLocaleDateString()}`;
            isValid = false;
          }
        }

        if (properties?.validation?.maxDate) {
          const maxDate = new Date(properties.validation.maxDate);
          if (selectedDate > maxDate) {
            errors.maxDate = true;
            errors.maxDateMessage = `Date and time must be on or before ${maxDate.toLocaleDateString()}`;
            isValid = false;
          }
        }
      } else {
        // Validate date format (date mode)
        const selectedDate = new Date(trimmedVal);
        if (isNaN(selectedDate.getTime())) {
          errors.invalidDate = true;
          isValid = false;
          return { isValid, errors };
        }
        
        // Date range validations for date
        if (properties?.validation?.minDate) {
          const minDate = new Date(properties.validation.minDate);
          if (selectedDate < minDate) {
            errors.minDate = true;
            errors.minDateMessage = `Date must be on or after ${minDate.toLocaleDateString()}`;
            isValid = false;
          }
        }

        if (properties?.validation?.maxDate) {
          const maxDate = new Date(properties.validation.maxDate);
          if (selectedDate > maxDate) {
            errors.maxDate = true;
            errors.maxDateMessage = `Date must be on or before ${maxDate.toLocaleDateString()}`;
            isValid = false;
          }
        }
      }
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    handleValueChange(newValue);
  };

  // Handle focus
  const handleFocus = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsFocused(false);
    setIsTouched(true);
    
    // Validate on blur and notify parent
    if (onValueChange) {
      const validation = validateField(value);
      const change: IFormControlChange = {
        id,
        value: value,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
    
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes
  useEffect(() => {
    if (formValue !== value && !isFocused) {
      setValue(formValue);
    }
  }, [formValue, value, isFocused]);

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get date classes
  const getDateClasses = (): string => {
    const base = 'form-date';
    const validation = validateField(value);
    const error = !validation.isValid && isTouched ? 'is-invalid' : '';
    return `${base} ${error} ${className}`.trim();
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;

  // Determine input type based on dateTimeMode
  const componentName = (properties as any).name || '';
  const dateTimeMode = (properties.basic as any)?.dateTimeMode || (componentName === 'datetime-picker' ? 'datetime' : 'date');
  const getInputType = (): string => {
    switch (dateTimeMode) {
      case 'time':
        return 'time';
      case 'datetime':
        return 'datetime-local';
      case 'date':
      default:
        return 'date';
    }
  };

  const inputType = getInputType();

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <input
          type={inputType}
          id={id}
          value={value}
          onChange={handleDateChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={shouldDisable}
          readOnly={readonly || properties?.validation?.readonly}
          min={properties?.validation?.minDate}
          max={properties?.validation?.maxDate}
          className={getDateClasses()}
        />

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(value)}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormDateTime;
